import {
  isPlatformBrowser,
  NgClass,
  NgStyle,
  NgTemplateOutlet
} from "@angular/common";
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation
} from "@angular/core";
import { CmsData } from "@dotcom-frontend/data";
import {
  LgGridColDirective,
  LgGridContainerDirective,
  LgGridRowDirective,
  LgIconComponent
} from "@legal-and-general/canopy";
import { DynamicComponent } from "../../utils/dynamic-loader/dynamic.component";
import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import { Store } from "@ngrx/store";
import { GlobalState } from "../../../store/global/global.reducer";
import { Observable } from "rxjs";
import {
  selectPageTitle,
  selectPageType
} from "../../../store/global/global.selectors";
import { DynamicComponentItem } from "../../utils/dynamic-loader/dynamic-component-item";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";

@Component({
  selector: "dotcom-frontend-grid-box-block",
  templateUrl: "./GridBoxBlock.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrl: "./GridBoxBlock.component.scss",
  standalone: true,
  imports: [
    LgGridRowDirective,
    LgGridContainerDirective,
    LgGridColDirective,
    LoaderComponent,
    LgIconComponent,
    NgClass,
    NgTemplateOutlet,
    NgStyle
  ]
})
export class GridBoxBlockComponent implements DynamicComponent, OnInit {
  @Input() data: CmsData | undefined;
  @ViewChild("sliderMain")
  sliderMain!: ElementRef;
  isBrowser = isPlatformBrowser(this.platformId);
  boxBlocks: any;
  gridLayout = 6;
  secondaryLayout = 3;
  layoutSelector!: string;
  twoOnTablet = false;
  currentRow = 0;
  isDualBox = false;
  isDualBoxOverlay = false;
  isFlipCards = false;
  followUpButtons: any;
  pageType: string | undefined;
  pageTitle = "";
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  pageType$: Observable<string> = this.store.select(selectPageType);
  headingComponent: DynamicComponentItem | undefined;
  backgroundColour = "";
  allowBackgroundFullWidthBleed = false;
  contentLayout: string | undefined;
  contentBgColor = "bg-color-";
  isParentBackgroundColour!: boolean;
  hasBoxBorders: boolean | undefined;
  lightBackgroundColours: string[] = ["white", "transparent"];
  sliderBarWidth = "100%";
  sliderBarPosition = "0";

  constructor(
    private componentLoaderService: ComponentLoaderService,
    private store: Store<{ global: GlobalState }>,
    @Inject(PLATFORM_ID) private platformId: any,
    private _elementRef: ElementRef
  ) {
    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });

    this.pageType$.subscribe((pageType) => {
      this.pageType = pageType;
    });
  }

  async ngOnInit() {
    if (this.data) {
      if (this.data["headingBlock"]) {
        this.headingComponent = await this.componentLoaderService.loadComponent(
          "Heading",
          "globals",
          {
            headingBlock: this.data["headingBlock"]
          }
        );
      }

      this.contentLayout = this.data["contentLayout"];

      if (this.data["layout"]) {
        this.layoutSelector = this.data["layout"];

        switch (this.data["layout"]) {
          case "equal-3":
          case "third-2":
            this.gridLayout = 4;
            break;
          case "equal-4":
            this.gridLayout = 3;
            this.twoOnTablet = true;
            break;
          case "large-small":
            this.gridLayout = 8;
            this.secondaryLayout = 4;
            this.layoutSelector = "alternating-grid";
            break;
          case "small-large":
            this.gridLayout = 4;
            this.secondaryLayout = 8;
            this.layoutSelector = "alternating-grid";
            break;
          case "slider-3":
          case "slider-2x2":
            this.gridLayout = 4;
            this.layoutSelector = "slider";
            if (this.isBrowser) {
              this.setSliderBarWidth(window.innerWidth);
            }
            break;
          default:
            this.gridLayout = 6;
        }
      }

      if (
        this.layoutSelector === "dual-box" ||
        this.layoutSelector === "dual-box-overlay"
      ) {
        this.isDualBox = true;
      }

      if (this.layoutSelector === "flip-card") {
        this.isFlipCards = true;
      }

      if (this.layoutSelector === "dual-box-overlay") {
        this.isDualBoxOverlay = true;
      }

      if (this.data["blocks"]) {
        this.boxBlocks = await this.componentLoaderService.contentAreaLoader(
          this.data["blocks"]
        );

        this.boxBlocks.forEach((element: any, i: string | number) => {
          if (this.boxBlocks[i]) {
            this.boxBlocks[i].data.isDualBox = this.isDualBox;
            this.boxBlocks[i].data.isDualBoxOverlay = this.isDualBoxOverlay;
            this.boxBlocks[i].data.isFlipCards = this.isFlipCards;
          }
        });
      }

      if (this.data["backgroundColour"]) {
        this.backgroundColour =
          this.contentBgColor + this.data["backgroundColour"];
        if (
          !this.lightBackgroundColours.includes(this.data["backgroundColour"])
        ) {
          this.isParentBackgroundColour = true;
        }
        if (
          this.data["addBoxBordersToBackgroundColour"] &&
          !this.lightBackgroundColours.includes(this.data["backgroundColour"])
        ) {
          this.hasBoxBorders = true;
        }
      }
      if (this.data["allowBackgroundFullWidthBleed"]) {
        this.contentBgColor =
          this.contentBgColor + this.data["backgroundColour"];
        if (
          !this.lightBackgroundColours.includes(this.data["backgroundColour"])
        ) {
          this.allowBackgroundFullWidthBleed = true;
        }
      }
      if (this.data["followUpButton"]) {
        this.followUpButtons = [];
        for (const button of this.data["followUpButton"]) {
          const buttonComponent =
            await this.componentLoaderService.loadComponent(
              "Button",
              "globals",
              {
                ...button,
                blockId : this.data["contentLink"]["id"],
                adobeData: {
                  interactionContainer: "Grid Block",
                  interactionLink: button["href"],
                  interactionName: button["text"],
                  interactionPage: this.pageTitle,
                  interactionRegion: "",
                  interactionType: "ButtonClick",
                  interactionDetails: ""
                }
              }
            );
          this.followUpButtons.push(buttonComponent);
        }
      }
    }
  }

  getCurrentRow(i: number) {
    if (i === 0) {
      this.currentRow = 1;
    } else if (i % 2 === 0) {
      this.currentRow++;
    }

    return this.currentRow;
  }

  alternateRows(currentRow: number, isEven: boolean) {
    return this.getCurrentRow(currentRow) % 2 === 0
      ? isEven
        ? this.secondaryLayout
        : this.gridLayout
      : isEven
        ? this.gridLayout
        : this.secondaryLayout;
  }

  scroll(direction: string) {
    const isTwoByTwo =
      this["data"] &&
      this.data["layout"] === "slider-2x2" &&
      window.innerWidth > 768
        ? 2
        : 1;
    const scrollIncrement =
      (this.sliderMain.nativeElement.scrollWidth / this.boxBlocks.length) *
      isTwoByTwo;
    let scrollPosition = this.sliderMain.nativeElement.scrollLeft;

    let sliderBarPosition;
    if (direction === "right") {
      scrollPosition =
        this.sliderMain.nativeElement.scrollLeft + scrollIncrement;
    } else {
      if (this.sliderMain.nativeElement.scrollLeft > 0) {
        scrollPosition =
          this.sliderMain.nativeElement.scrollLeft - scrollIncrement;
      }
    }

    if (sliderBarPosition) {
      this.sliderBarPosition = sliderBarPosition;
    }

    this.sliderMain.nativeElement.scrollLeft = scrollPosition;
  }

  onScroll() {
    if (
      Math.floor(this.sliderMain.nativeElement.scrollLeft) ===
      Math.floor(
        this.sliderMain.nativeElement.scrollWidth -
          this.sliderMain.nativeElement.clientWidth
      )
    ) {
      this.sliderBarPosition = `calc(100% - ${this.sliderBarWidth})`;
    } else {
      this.sliderBarPosition =
        (this.sliderMain.nativeElement.scrollLeft * 100) /
          this.sliderMain.nativeElement.scrollWidth +
        "%";
    }
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: any) {
    this.setSliderBarWidth(event.target.innerWidth);
  }

  setSliderBarWidth(windowWidth: number) {
    let lengthMultiplier = 0;

    if (this.isBrowser && this.data) {
      const isTwoByTwo = this.data["layout"] === "slider-2x2";
      if (windowWidth < 768) {
        lengthMultiplier = 1;
      } else if (windowWidth >= 768 && windowWidth < 1024) {
        if (isTwoByTwo) {
          lengthMultiplier = 4;
        } else {
          lengthMultiplier = 2;
        }
      } else {
        if (isTwoByTwo) {
          lengthMultiplier = 4;
        } else {
          lengthMultiplier = 3;
        }
      }

      this.sliderBarWidth =
        Math.round((100 / this.data["blocks"].length) * lengthMultiplier) + "%";
    }
  }
}
